import { DictionaryProvider, DictionaryConsumer } from "./Dictionary";
import { Scalar, ScalarProvider, ScalarConsumer, scalarValue } from "./Scalar";

class ReductionStep<ValueType, ResultType> extends ScalarProvider<ResultType> implements ScalarConsumer<ResultType> {
	private _result: ResultType;
	private _value_subscription: ScalarConsumer<ValueType> | undefined;

	constructor(
		public source: Scalar<ResultType>,
		private _value: Scalar<ValueType>,
		private readonly fn: (accumulator: ResultType, value: ValueType) => ResultType,
		public subscriber: ScalarConsumer<ResultType>,
		readonly __origin?: string
	) {
		super();
		if (this._value instanceof ScalarProvider) {
			this._value_subscription = this._value.subscribe({
				update: (value) => this.updateValue(value),
			});
		}
		if (source instanceof ScalarProvider) {
			source.subscribe(this);
		} else {
			this.evaluate(source, scalarValue(this._value));
		}
	}

	get value() {
		return scalarValue(this._result);
	}

	setSource(source: Scalar<ResultType>) {
		this.source = source;
	}

	private evaluate(accumulator: ResultType, value: ValueType) {
		const new_result = this.fn(accumulator, value);
		if (new_result === this._result) return;
		this._result = new_result;
		this.subscriber.update(this._result);
	}

	private updateValue(value: ValueType) {
		this.evaluate(scalarValue(this.source), value);
	}

	update(accumulator: ResultType) {
		this.evaluate(accumulator, scalarValue(this._value));
	}

	subscribe(subscriber: ScalarConsumer<ResultType>): typeof subscriber {
		this.subscriber = subscriber;
		subscriber.update(this._result);
		return subscriber;
	}

	unsubscribe() {
		if (this._value instanceof ScalarProvider) {
			this._value.unsubscribe(this._value_subscription!);
			this._value_subscription = undefined;
		}
	}
}

export class Reduce<KeyType, ValueType, ResultType>
	extends ScalarProvider<ResultType>
	implements DictionaryConsumer<KeyType, Scalar<ValueType>>, ScalarConsumer<DictionaryProvider<KeyType, ValueType>>
{
	private _subscriber: ScalarConsumer<ResultType> | undefined;
	private _value: Scalar<ResultType>;
	private _steps = new Map<KeyType, ReductionStep<ValueType, ResultType>>();
	private _dictionary_source: DictionaryProvider<KeyType, ValueType> | undefined;

	constructor(
		readonly scalar_source: Scalar<DictionaryProvider<KeyType, ValueType>>,
		readonly fn: (accumulator: ResultType, value: ValueType) => ResultType,
		readonly initial_value: ResultType,
		readonly __origin?: string
	) {
		super();
		this._value = initial_value;
	}

	get value() {
		if (this._subscriber) {
			return scalarValue(this._value!);
		} else {
			let result = this.initial_value;
			for (const value of [...scalarValue(this.scalar_source).entries.values()]) {
				result = this.fn(result, scalarValue(value));
			}
			return result;
		}
	}

	update(value: DictionaryProvider<KeyType, ValueType>) {
		this._dictionary_source?.unsubscribe(this);
		this._dictionary_source = value;
		for (const step of this._steps.values()) {
			step.unsubscribe();
		}
		this._steps.clear();
		this._value = this.initial_value;
		this._dictionary_source.subscribe(this);
		if (this._dictionary_source.size.value === 0) {
			this._subscriber!.update(this.initial_value);
		}
	}

	add(key: KeyType, value: Scalar<ValueType>) {
		const new_value = new ReductionStep(this._value, value, this.fn, this._subscriber!, this.__origin);
		this._steps.set(key, new_value);
		this._value = new_value;
	}

	remove(key: KeyType) {
		const step = this._steps.get(key)!;
		this._steps.delete(key);
		if (this._value === step) {
			this._value = step.source;
		}
		if (step.subscriber instanceof ReductionStep) {
			step.subscriber.setSource(step.source);
		}
		if (step.source instanceof ScalarProvider) {
			step.source.subscribe(step.subscriber);
		} else {
			step.subscriber.update(step.source);
		}
		step.unsubscribe();
	}

	subscribe(subscriber: ScalarConsumer<ResultType>) {
		if (this._subscriber !== undefined) throw new Error("unexpected");
		this._subscriber = subscriber;
		if (this.scalar_source instanceof ScalarProvider) {
			this.scalar_source.subscribe(this);
		} else {
			this.update(this.scalar_source);
		}
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<ResultType>) {
		if ((<any>global)?.alan_strict && this._subscriber !== subscriber) throw new Error("unexpected");
		this._subscriber = undefined;

		if (this.scalar_source instanceof ScalarProvider) {
			this.scalar_source.unsubscribe(this);
		}
		this._dictionary_source!.unsubscribe(this);
		this._dictionary_source = undefined;

		for (const step of this._steps.values()) {
			step.unsubscribe();
		}
		this._steps.clear();
		this._value = this.initial_value;
	}
}
